import Api from "../../../services/api";
import { COACHING_SESSION_STATUS } from "../constants";
import { buildAuthorName } from "../helpers/buildAuthorName";
import { fetchRuleNames } from "../helpers/fetchRuleNames";
import {
    CoachingSessionStatus,
    ICoachingComment,
    ICoachingSession,
    IRawCoachingSession,
    IRawCoachingSessionComment,
    IRawExceptionEvent
} from "../store/types";

function groupExceptionEvents(
    events: IRawExceptionEvent[] | undefined,
    ruleNameMap: Map<string, string>
): { ruleType: string; count: number }[] {
    if (!events?.length) {
        return [];
    }

    const counts = new Map<string, number>();

    for (const event of events) {
        const ruleType = event.rule?.id
            ? ruleNameMap.get(event.rule.id)
            : undefined;
        if (ruleType) {
            counts.set(ruleType, (counts.get(ruleType) ?? 0) + 1);
        }
    }

    return Array.from(counts, ([ruleType, count]) => ({
        ruleType,
        count
    })).sort((a, b) => b.count - a.count);
}

function mapRawComment(raw: IRawCoachingSessionComment): ICoachingComment {
    return {
        id: raw.id,
        authorName: buildAuthorName(raw.user),
        dateTime: raw.dateTime,
        comment: raw.text
    };
}

function mapRawSession(
    raw: IRawCoachingSession,
    ruleNameMap: Map<string, string>
): ICoachingSession {
    return {
        id: raw.id,
        status: raw.state as CoachingSessionStatus,
        assignedDate: raw.createdDateTime,
        modifiedDate: raw.modifiedDateTime,
        acknowledgementDateTime: raw.acknowledgementDateTime,
        eventSummaries: groupExceptionEvents(raw.exceptionEvents, ruleNameMap)
    };
}

function collectSessionRuleIds(sessions: IRawCoachingSession[]): string[] {
    const ids: string[] = [];
    for (const session of sessions) {
        for (const event of session.exceptionEvents ?? []) {
            if (event.rule?.id) {
                ids.push(event.rule.id);
            }
        }
    }
    return ids;
}

export class CoachingSessionService {
    constructor(private readonly api: Api) {}

    async getCoachingSessions(): Promise<ICoachingSession[]> {
        const user = this.api.getUser();
        if (!user?.id) {
            return [];
        }

        const results: IRawCoachingSession[] = await this.api.callPromise(
            "Get",
            {
                typeName: "CoachingSession",
                search: {
                    userSearch: { id: user.id },
                    states: [
                        COACHING_SESSION_STATUS.NEW,
                        COACHING_SESSION_STATUS.ACTIVE,
                        COACHING_SESSION_STATUS.COMPLETED
                    ]
                }
            }
        );

        if (!results?.length) {
            return [];
        }

        const ruleNameMap = await fetchRuleNames(
            collectSessionRuleIds(results),
            this.api
        );
        return results.map((raw) => mapRawSession(raw, ruleNameMap));
    }

    async createCoachingSession(userId: string): Promise<string> {
        return this.api.callPromise("Add", {
            typeName: "CoachingSession",
            entity: {
                driver: { id: userId },
                state: COACHING_SESSION_STATUS.ACTIVE
            }
        });
    }

    /** One Get for the driver's open coaching work (Active and/or virtual New sessions). */
    async getOpenSessions(userId: string): Promise<IRawCoachingSession[]> {
        const results: IRawCoachingSession[] = await this.api.callPromise("Get", {
            typeName: "CoachingSession",
            search: {
                userSearch: { id: userId },
                states: [COACHING_SESSION_STATUS.ACTIVE, COACHING_SESSION_STATUS.NEW]
            }
        });
        return results ?? [];
    }

    /** Fetches a single coaching session by id, or undefined when it does not exist. */
    async getSession(sessionId: string): Promise<IRawCoachingSession | undefined> {
        const results: IRawCoachingSession[] = await this.api.callPromise("Get", {
            typeName: "CoachingSession",
            search: { id: sessionId }
        });
        return results?.[0];
    }

    async getComments(sessionId: string): Promise<ICoachingComment[]> {
        const results: IRawCoachingSessionComment[] =
            await this.api.callPromise("Get", {
                typeName: "CoachingSessionComment",
                search: {
                    coachingSessionId: sessionId
                }
            });

        if (!results?.length) {
            return [];
        }

        return results.map(mapRawComment);
    }

    async completeSession(sessionId: string, acknowledgementDateTime?: string): Promise<void> {
        await this.api.callPromise("Set", {
            typeName: "CoachingSession",
            entity: {
                id: sessionId,
                acknowledgementDateTime: acknowledgementDateTime ?? new Date().toISOString()
            }
        });
    }

    async addComment(sessionId: string, text: string, id?: string): Promise<string> {
        return this.api.callPromise("Add", {
            typeName: "CoachingSessionComment",
            entity: {
                // Optional client id makes the Add idempotent: the server honors it, so a
                // retry of the same id throws DuplicateException (CoachingCommentSync treats
                // it as already-synced). Used by the online write path's queue fallback to
                // avoid duplicates on a lost POST response (MYG-132907).
                ...(id ? { id } : {}),
                coachingSessionId: sessionId,
                text
            }
        });
    }
}
